import { Injectable, NgZone, inject } from '@angular/core';
import {
  BehaviorSubject,
  Subject,
  filter,
  from,
  fromEvent,
  mergeMap,
  take,
  tap,
  throttleTime,
} from 'rxjs';
import { environment } from '../environment';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class IdleService {
  private idleTime = 59 * 60 * 1000;
  private sessionTime = 60 * 1000;

  private idleTimeout;
  private sessionTimeout;

  private idleSubject = new BehaviorSubject<boolean>(false);
  public idle$ = this.idleSubject.asObservable();

  // Emitterar precis innan utloggning faktiskt sker (till skillnad från idle$ som emitterar redan vid inaktivitetsgränsen)
  private loggingOutSubject = new Subject<void>();
  public loggingOut$ = this.loggingOutSubject.asObservable();

  private latestTime: number;
  private isLoggingOut = false;

  private authService = inject(AuthService);
  private zone = inject(NgZone);
  private env = inject(environment);

  initIdle() {
    this.latestTime = Date.now();
    this.startIdleTimeout();
    return this.getInterrupts();
  }

  private getInterrupts() {
    // default interrupts som används i ng-idle
    const documentEvents = [
      'mousemove',
      'keydown',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'scroll',
    ];

    return from(documentEvents).pipe(
      mergeMap((event) => fromEvent(document, event)),
      throttleTime(1000),
      filter((event) => !this.filterDocumentEvent(event)),
      tap(() => {
        this.checkAndSetLatestTime();
        this.startIdleTimeout();
        if (this.idleSubject.value) {
          this.idleSubject.next(false);
          this.resetSessionTimeout();
        }
      }),
    );
  }

  private startIdleTimeout() {
    this.zone.runOutsideAngular(() => {
      if (this.idleTimeout) {
        clearTimeout(this.idleTimeout);
      }
      this.idleTimeout = setTimeout(() => {
        this.idleSubject.next(true);
        this.setSessionTimeout();
      }, this.idleTime);
    });
  }

  // filter från ng-idle
  private filterDocumentEvent(event): boolean {
    // some browser bad input hacks
    return (
      event.type === 'mousemove' &&
      // fix for Chrome destop notifications
      ((event.originalEvent &&
        event.originalEvent.movementX === 0 &&
        event.originalEvent.movementY === 0) ||
        // fix for webkit fake mousemove
        (event.movementX !== void 0 && !event.movementX) ||
        !event.movementY)
    );
  }

  private setSessionTimeout() {
    this.zone.runOutsideAngular(() => {
      this.sessionTimeout = setTimeout(() => {
        if (this.idleSubject.value) {
          this.logoutForTimeout();
        }
      }, this.sessionTime);
    });
  }

  private resetSessionTimeout() {
    this.zone.runOutsideAngular(() => {
      if (this.sessionTimeout) {
        clearTimeout(this.sessionTimeout);
        this.sessionTimeout = undefined;
      }
    });
  }

  private checkAndSetLatestTime() {
    const newTime = Date.now();
    if (newTime > this.latestTime + this.idleTime + this.sessionTime) {
      this.logoutForTimeout();
    } else {
      this.latestTime = newTime;
    }
  }

  private logoutForTimeout() {
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;
    this.loggingOutSubject.next();
    this.authService
      .logout(`${this.env.etjansterCommonUrl}/public/logout?reason=timeout`)
      .pipe(take(1))
      .subscribe();
  }
}
